import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "standard" });
  const [saving, setSaving] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearSel, setClearSel] = useState({ dividends: true, shareholders: true, audit: true, companies: false });
  const [confirmText, setConfirmText] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "standard" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };
  const changeRole = async (u, role) => {
    await api.patch(`/users/${u.id}`, { role });
    load();
  };

  const runClear = async () => {
    setClearing(true);
    try {
      const res = await api.post("/admin/clear-data", clearSel);
      const d = res.data.deleted || {};
      const parts = Object.entries(d).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Cleared: ${parts || "nothing selected"}`);
      setClearOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClearing(false);
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users" description="Manage internal user access.">
        <Button onClick={() => setOpen(true)} data-testid="add-user-button" className="bg-cass-blue text-white hover:bg-cass-bluehover">
          <Plus className="mr-2 h-4 w-4" /> Add User
        </Button>
      </PageHeader>

      <Card className="border-cass-border shadow-sm">
        {users === null ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div> : (
          <Table>
            <TableHeader>
              <TableRow className="bg-cass-surface hover:bg-cass-surface">
                <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-cass-surface" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium text-cass-navy">{u.name}</TableCell>
                  <TableCell className="text-cass-navy/70">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="w-36" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="standard">Standard user</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Badge className={`border-0 ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{u.active ? "Active" : "Disabled"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>{u.active ? "Disable" : "Enable"}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="font-head text-cass-navy">Add User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" data-testid="user-password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="admin">Administrator</SelectItem><SelectItem value="standard">Standard user</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user" className="bg-cass-blue text-white hover:bg-cass-bluehover">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="mt-8 border-red-200 bg-red-50/40 p-6 shadow-sm" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div className="flex-1">
            <h3 className="font-head text-lg font-semibold text-cass-navy">Clear data</h3>
            <p className="mt-1 max-w-2xl text-sm text-cass-navy/60">
              Permanently delete records to remove test data. This cannot be undone. User accounts are never deleted.
              Choose what to remove, then confirm.
            </p>
            <Button variant="outline" onClick={() => setClearOpen(true)} data-testid="open-clear-data"
              className="mt-4 border-red-300 text-red-600 hover:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" /> Clear Data…
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="font-head text-cass-navy">Clear data</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-cass-navy/70">Select the records to permanently delete:</p>
            {[
              { key: "dividends", label: "All dividend records" },
              { key: "shareholders", label: "All shareholders" },
              { key: "audit", label: "Audit trail history" },
              { key: "companies", label: "All companies (including branding)" },
            ].map((opt) => (
              <label key={opt.key} className="flex items-center gap-3 rounded-md border border-cass-border p-3">
                <Checkbox checked={clearSel[opt.key]} onCheckedChange={(v) => setClearSel((s) => ({ ...s, [opt.key]: !!v }))} data-testid={`clear-${opt.key}`} />
                <span className="text-sm text-cass-navy/80">{opt.label}</span>
              </label>
            ))}
            <div className="space-y-1.5 pt-1">
              <Label className="text-sm text-cass-navy/80">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="clear-confirm-input" placeholder="DELETE" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearOpen(false)}>Cancel</Button>
            <Button onClick={runClear} data-testid="confirm-clear-data"
              disabled={clearing || confirmText !== "DELETE" || !Object.values(clearSel).some(Boolean)}
              className="bg-red-600 text-white hover:bg-red-700">
              {clearing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
